import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Stack, Divider, Button, Grid,
  Paper, Avatar, CircularProgress, Alert, Tooltip,
  IconButton, Chip, LinearProgress, InputAdornment, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, List, ListItem, ListItemAvatar, ListItemText, ListItemSecondaryAction,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBusById, assignStudentsToBus } from '../slices/busesSlice';
import { fetchStudents } from '../slices/studentsSlice';
import { fetchClasses } from '../slices/classesSlice';
import { useToast } from '../context/ToastContext';
import {
  DirectionsBus as BusIcon,
  Person as PersonIcon,
  EventSeat as SeatIcon,
  People as PeopleIcon,
  Info as InfoIcon,
  Phone as PhoneIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  Group as GroupIcon,
  Receipt as ReceiptIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { exportToExcel, exportBusDetailsToPrint, exportStudentsListToPrint } from '../utils/exportUtils';

// ── helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateString) => {
  if (!dateString) return 'غير محدد';
  try { return new Date(dateString).toLocaleDateString('en-GB'); } catch { return dateString; }
};

const FONT = 'Cairo, Tajawal, sans-serif';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, ease: 'easeOut', delay },
});

// ── sub-components ────────────────────────────────────────────────────────────

const SectionCard = ({ children, sx = {} }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 3,
      border: '1px solid #E5E7EB',
      p: { xs: 2, sm: 3 },
      background: '#fff',
      boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
      fontFamily: FONT,
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const SectionHeader = ({ icon, title, color = '#4338CA', tint, action }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
    <Stack useFlexGap direction="row" alignItems="center" spacing={1.2}>
      <Box sx={{
        width: 38, height: 38, borderRadius: 2,
        background: tint || `${color}18`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color, flexShrink: 0,
      }}>
        {icon}
      </Box>
      <Typography variant="h6" fontWeight={700} color={color} sx={{ fontFamily: FONT }}>{title}</Typography>
    </Stack>
    {action}
  </Stack>
);

const InfoRow = ({ icon, label, value, color = '#4338CA' }) => (
  <Stack useFlexGap direction="row" alignItems="flex-start" spacing={1.5} sx={{ py: 0.6 }}>
    <Box sx={{ color, mt: 0.1, flexShrink: 0 }}>{icon}</Box>
    <Box>
      <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT }} display="block" lineHeight={1.2}>{label}</Typography>
      <Typography variant="body1" fontWeight={600} sx={{ color: '#1E1B4B', fontFamily: FONT }}>
        {(value !== null && value !== undefined && value !== '') ? value : <span style={{ color: '#9CA3AF', fontSize: '0.85rem' }}>غير محدد</span>}
      </Typography>
    </Box>
  </Stack>
);

const StatCard = ({ icon, label, value, color, bg, sub }) => (
  <Paper elevation={0} sx={{
    flex: 1, minWidth: 130, p: 2, borderRadius: 3,
    background: bg || `${color}11`,
    border: `1px solid ${color}33`,
    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
    fontFamily: FONT,
  }}>
    <Box sx={{
      color, mb: 0.3, width: 40, height: 40, borderRadius: 2,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: bg ? 'rgba(255,255,255,0.6)' : `${color}18`,
    }}>{icon}</Box>
    <Typography variant="h5" fontWeight={800} color={color} sx={{ fontFamily: FONT }}>{value}</Typography>
    <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT }} fontWeight={700}>{label}</Typography>
    {sub && <Typography variant="caption" sx={{ color: '#9CA3AF', fontFamily: FONT }}>{sub}</Typography>}
  </Paper>
);

const headerBtnSx = {
  bgcolor: 'rgba(255,255,255,0.18)',
  color: '#fff',
  borderRadius: 2.5,
  fontWeight: 700,
  textTransform: 'none',
  fontFamily: FONT,
  backdropFilter: 'blur(4px)',
  border: '1px solid rgba(255,255,255,0.35)',
  boxShadow: 'none',
  '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' },
};

// ── main component ────────────────────────────────────────────────────────────

const ViewBusPage = () => {
  const { id: busId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [studentSearch, setStudentSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSearch, setDialogSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const { selectedBus: bus, selectedBusStatus, selectedBusError } = useSelector((state) => state.buses);
  const { students: allStudents, status: studentsStatus } = useSelector((state) => state.students);
  const { list: classesList, status: classesStatus } = useSelector((state) => state.classes);

  useEffect(() => {
    if (busId) dispatch(fetchBusById(busId));
    dispatch(fetchStudents());
    if (classesStatus === 'idle') dispatch(fetchClasses());
  }, [dispatch, busId, classesStatus]);

  const classMap = useMemo(() => {
    if (classesStatus !== 'succeeded') return new Map();
    return new Map(classesList.map(cls => [cls.id, cls.name]));
  }, [classesList, classesStatus]);

  const assignedStudents = useMemo(() => bus?.students || [], [bus]);

  const filteredStudents = useMemo(() => {
    if (!studentSearch.trim()) return assignedStudents;
    const q = studentSearch.trim().toLowerCase();
    return assignedStudents.filter(s =>
      `${s.first_name || ''} ${s.second_name || ''}`.toLowerCase().includes(q)
    );
  }, [assignedStudents, studentSearch]);

  const latestPayment = useMemo(() =>
    bus?.payments?.length
      ? bus.payments.reduce((latest, p) => new Date(p.date) > new Date(latest.date) ? p : latest)
      : null,
    [bus]
  );

  const totalPaid = useMemo(() =>
    (bus?.payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0),
    [bus]
  );

  const getContact = (bus) => {
    if (bus.bus_type === 'داخلي' && bus.driver_phone && bus.driver_phone !== 'غير محدد') return bus.driver_phone;
    return bus.phone_number || 'غير محدد';
  };

  const getDriverName = (bus) => bus.bus_type === 'داخلي' ? bus.driver_name : bus.manager_name;

  const occupancyPct = bus?.capacity ? Math.min(100, Math.round((assignedStudents.length / bus.capacity) * 100)) : 0;
  const occupancyColor = occupancyPct >= 90 ? '#DC2626' : occupancyPct >= 70 ? '#D97706' : '#16A34A';

  const handleExport = () => {
    if (!bus) return;
    const columns = [
      { header: 'رقم اللوحة', key: 'bus_number' },
      { header: 'اسم السائق', key: 'driver_or_manager_name' },
      { header: 'السعة', key: 'capacity' },
      { header: 'عدد الطلاب', key: 'students_count' },
      { header: 'النوع', key: 'bus_type' },
      { header: 'الهاتف', key: 'phone_number' },
    ];
    const data = [{
      bus_number: bus.bus_number || '',
      driver_or_manager_name: getDriverName(bus),
      capacity: bus.capacity ? `${bus.capacity} طالب` : '',
      students_count: assignedStudents.length,
      bus_type: bus.bus_type === 'Internal' || bus.bus_type === 'داخلي' ? 'داخلي' : 'خارجي',
      phone_number: bus.phone_number ?? bus.driver_phone ?? '',
    }];
    exportToExcel(data, columns, 'bus_details');
  };

  const handlePrint = () => { if (bus) exportBusDetailsToPrint(bus, assignedStudents, classMap); };

  const handlePrintStudents = () => {
    if (!assignedStudents.length) { showToast('لا يوجد طلاب لطباعتهم', 'warning'); return; }
    exportStudentsListToPrint(assignedStudents, classMap, bus);
  };

  const openAssignDialog = () => {
    dispatch(fetchStudents());
    const alreadyAssigned = (bus?.students || []).map(s => s.id);
    setSelectedIds(alreadyAssigned);
    setDialogSearch('');
    setDialogOpen(true);
  };

  const handleToggleStudent = (studentId) => {
    setSelectedIds(prev =>
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };

  const handleSaveAssignments = async () => {
    const previousIds = (bus?.students || []).map(s => s.id);
    const toAdd = selectedIds.filter(id => !previousIds.includes(id));
    const toRemove = previousIds.filter(id => !selectedIds.includes(id));

    setIsSaving(true);
    try {
      if (toAdd.length > 0) {
        await dispatch(assignStudentsToBus({ busId, studentIds: toAdd, action: 'add' })).unwrap();
      }
      if (toRemove.length > 0) {
        await dispatch(assignStudentsToBus({ busId, studentIds: toRemove, action: 'remove' })).unwrap();
      }
      showToast('تم تحديث قائمة الطلاب بنجاح', 'success');
      setDialogOpen(false);
      dispatch(fetchBusById(busId));
    } catch {
      showToast('حدث خطأ أثناء تحديث الطلاب', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveStudent = async (studentId) => {
    try {
      await dispatch(assignStudentsToBus({ busId, studentIds: [studentId], action: 'remove' })).unwrap();
      showToast('تم إزالة الطالب من الحافلة', 'success');
      dispatch(fetchBusById(busId));
    } catch {
      showToast('حدث خطأ أثناء إزالة الطالب', 'error');
    }
  };

  const dialogStudents = useMemo(() => {
    if (!allStudents) return [];
    const active = allStudents.filter(s => !s.is_archived && !s.isArchived);
    if (!dialogSearch.trim()) return active;
    const q = dialogSearch.trim().toLowerCase();
    return active.filter(s => `${s.first_name || ''} ${s.second_name || ''}`.toLowerCase().includes(q));
  }, [allStudents, dialogSearch]);

  // ── loading / error ────────────────────────────────────────────────────────

  if (selectedBusStatus === 'loading' || studentsStatus === 'loading' || classesStatus === 'loading') {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" gap={2}
        sx={{ direction: 'rtl', bgcolor: '#F8F9FF', fontFamily: FONT }}>
        <CircularProgress size={52} thickness={3} sx={{ color: '#4338CA' }} />
        <Typography sx={{ color: '#6B7280', fontFamily: FONT }}>جاري تحميل البيانات…</Typography>
      </Box>
    );
  }

  if (selectedBusStatus === 'failed' || studentsStatus === 'failed' || classesStatus === 'failed') {
    return (
      <Box sx={{ direction: 'rtl', minHeight: '100vh', bgcolor: '#F8F9FF', fontFamily: FONT, p: 4 }}>
        <Box sx={{ maxWidth: 520, mx: 'auto', textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 3, fontFamily: FONT }}>
            فشل تحميل بعض البيانات. يرجى المحاولة مرة أخرى.
            {selectedBusError && <><br /><Typography variant="caption" sx={{ fontFamily: FONT }}>{selectedBusError}</Typography></>}
          </Alert>
          <Stack useFlexGap direction="row" spacing={1.5} justifyContent="center">
            <Button variant="outlined" onClick={() => navigate('/buses')} startIcon={<ArrowBackIcon />}
              sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, borderColor: '#4338CA', color: '#4338CA', '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF' } }}>
              العودة
            </Button>
            <Button variant="contained" onClick={() => {
              if (selectedBusStatus !== 'succeeded') dispatch(fetchBusById(busId));
              if (studentsStatus !== 'succeeded') dispatch(fetchStudents());
              if (classesStatus !== 'succeeded') dispatch(fetchClasses());
            }} sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, bgcolor: '#4338CA', boxShadow: 'none', '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' } }}>
              إعادة المحاولة
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  if (!bus) {
    return (
      <Box sx={{ direction: 'rtl', minHeight: '100vh', bgcolor: '#F8F9FF', fontFamily: FONT, p: 4, textAlign: 'center' }}>
        <Typography sx={{ color: '#6B7280', fontFamily: FONT }} gutterBottom>لم يتم العثور على بيانات الحافلة.</Typography>
        <Button variant="outlined" onClick={() => navigate('/buses')} startIcon={<ArrowBackIcon />}
          sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, mt: 1, borderColor: '#4338CA', color: '#4338CA', '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF' } }}>
          العودة
        </Button>
      </Box>
    );
  }

  const busType = bus.bus_type === 'Internal' || bus.bus_type === 'داخلي' ? 'داخلي' : 'خارجي';

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    // 🎨 Design System Applied
    <>
    <Box dir="rtl" sx={{ direction: 'rtl', fontFamily: FONT, p: { xs: 2, md: 3 }, pb: 6, minHeight: '100vh', bgcolor: '#F8F9FF' }}>
     <Box sx={{ maxWidth: 1400, mx: 'auto' }}>

      {/* ── Hero Header ─────────────────────────────────────────────────── */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <Paper elevation={0} sx={{
          p: { xs: 2.5, sm: 3.5 }, mb: 3, borderRadius: 3,
          background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
          color: '#fff', position: 'relative', overflow: 'hidden',
        }}>
          {/* decorative circle */}
          <Box sx={{
            position: 'absolute', right: -70, top: -70,
            width: 260, height: 260, borderRadius: '50%',
            background: 'rgba(255,255,255,0.09)', pointerEvents: 'none',
          }} />

          <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between" spacing={2}>
            {/* Left: icon + name */}
            <Stack useFlexGap direction="row" alignItems="center" spacing={2}>
              <Box sx={{
                width: 64, height: 64, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <BusIcon sx={{ fontSize: 36, color: '#fff' }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={800} color="#fff" lineHeight={1.2} sx={{ fontFamily: FONT }}>
                  {bus.name || bus.bus_number || 'الحافلة'}
                </Typography>
                <Stack useFlexGap direction="row" spacing={1} mt={0.75}>
                  <Chip label={busType} size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: '#fff', fontWeight: 700, fontSize: '0.72rem', fontFamily: FONT }} />
                  {bus.bus_number && (
                    <Chip label={bus.bus_number} size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: '0.72rem', fontFamily: FONT }} />
                  )}
                </Stack>
              </Box>
            </Stack>

            {/* Right: actions */}
            <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap">
              <Tooltip title="تصدير Excel">
                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport} size="small" sx={headerBtnSx}>
                  Excel
                </Button>
              </Tooltip>
              <Tooltip title="طباعة">
                <Button variant="contained" startIcon={<PictureAsPdfIcon />} onClick={handlePrint} size="small" sx={headerBtnSx}>
                  طباعة
                </Button>
              </Tooltip>
              <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/buses/edit/${busId}`)}
                size="small" sx={headerBtnSx}>
                تعديل
              </Button>
              <Tooltip title="العودة">
                <IconButton onClick={() => navigate('/buses')} size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.28)' } }}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Paper>
      </motion.div>

      {/* ── Stat Cards Row ───────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.07)}>
        <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: 3 }}>
          <StatCard icon={<SeatIcon />} label="السعة الكلية" value={bus.capacity ?? '—'} color="#4338CA" bg="#EEF2FF" sub="طالب" />
          <StatCard icon={<PeopleIcon />} label="الطلاب المسجلون" value={assignedStudents.length} color="#16A34A" bg="#DCFCE7" sub="طالب" />
          <StatCard icon={<ReceiptIcon />} label="إجمالي المدفوعات" value={`${totalPaid.toLocaleString('en-US')} ₪`} color="#0284C7" bg="#E0F2FE" />
          <StatCard icon={<InfoIcon />} label="النوع" value={busType} color="#D97706" bg="#FEF3C7" />
          <StatCard icon={<PhoneIcon />} label="التواصل" value={getContact(bus)} color="#5B21F4" bg="#F3EEFF" />
        </Stack>
      </motion.div>

      {/* ── Occupancy Bar ────────────────────────────────────────────────── */}
      {bus.capacity > 0 && (
        <motion.div {...fadeUp(0.1)}>
          <SectionCard sx={{ mb: 3, p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: FONT }}>
                نسبة الإشغال
              </Typography>
              <Typography variant="body2" fontWeight={800} color={occupancyColor} sx={{ fontFamily: FONT }}>
                {assignedStudents.length} / {bus.capacity} ({occupancyPct}%)
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={occupancyPct}
              sx={{
                height: 10, borderRadius: 5,
                bgcolor: `${occupancyColor}22`,
                '& .MuiLinearProgress-bar': { bgcolor: occupancyColor, borderRadius: 5 },
              }}
            />
          </SectionCard>
        </motion.div>
      )}

      <Grid container spacing={2.5} alignItems="stretch">

        {/* ── Bus Info ──────────────────────────────────────────────────── */}
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <motion.div {...fadeUp(0.13)} style={{ display: 'flex', flex: 1 }}>
            <SectionCard sx={{ flex: 1 }}>
              <SectionHeader icon={<InfoIcon fontSize="small" />} title="معلومات أساسية" />
              <Stack spacing={1.2}>
                <InfoRow icon={<BusIcon fontSize="small" />} label="رقم اللوحة" value={bus.bus_number} />
                <InfoRow icon={<PersonIcon fontSize="small" />} label={bus.bus_type === 'داخلي' ? 'اسم السائق' : 'اسم المدير'} value={getDriverName(bus)} />
                <InfoRow icon={<PhoneIcon fontSize="small" />} label="رقم الهاتف" value={getContact(bus)} />
                <InfoRow icon={<InfoIcon fontSize="small" />} label="نوع الحافلة" value={busType} />
                <InfoRow icon={<SeatIcon fontSize="small" />} label="السعة القصوى" value={bus.capacity ? `${bus.capacity} طالب` : null} />
                <InfoRow icon={<PeopleIcon fontSize="small" />} label="الطلاب الحاليون" value={`${assignedStudents.length} طالب`} />
              </Stack>
            </SectionCard>
          </motion.div>
        </Grid>

        {/* ── Payments ──────────────────────────────────────────────────── */}
        <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
          <motion.div {...fadeUp(0.16)} style={{ display: 'flex', flex: 1 }}>
            <SectionCard sx={{ flex: 1 }}>
              {/* Header row */}
              <SectionHeader icon={<ReceiptIcon fontSize="small" />} title="سجل المدفوعات" color="#0284C7" tint="#E0F2FE" />

              {bus.payments && bus.payments.length > 0 ? (
                <>
                  {/* Summary strip */}
                  <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
                    <Paper elevation={0} sx={{
                      flex: 1, p: 2, borderRadius: 2.5, textAlign: 'center',
                      background: '#E0F2FE', border: '1px solid #BAE6FD',
                    }}>
                      <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT }} fontWeight={700} display="block">
                        إجمالي المدفوعات
                      </Typography>
                      <Typography variant="h6" fontWeight={800} color="#0284C7" sx={{ fontFamily: FONT }}>
                        {totalPaid.toLocaleString('en-US')} ₪
                      </Typography>
                    </Paper>
                    <Paper elevation={0} sx={{
                      flex: 1, p: 2, borderRadius: 2.5, textAlign: 'center',
                      background: '#DCFCE7', border: '1px solid #BBF7D0',
                    }}>
                      <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT }} fontWeight={700} display="block">
                        عدد الدفعات
                      </Typography>
                      <Typography variant="h6" fontWeight={800} color="#16A34A" sx={{ fontFamily: FONT }}>
                        {bus.payments.length}
                      </Typography>
                    </Paper>
                    {latestPayment && (
                      <Paper elevation={0} sx={{
                        flex: 2, p: 2, borderRadius: 2.5,
                        background: '#EEF2FF', border: '1px solid #E0E7FF',
                        display: 'flex', alignItems: 'center', gap: 2,
                      }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT }} fontWeight={700} display="block">
                            آخر دفعة
                          </Typography>
                          <Typography fontWeight={800} color="#4338CA" sx={{ fontFamily: FONT }}>
                            {parseFloat(latestPayment.amount).toLocaleString('en-US')} ₪
                          </Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem sx={{ borderColor: '#E5E7EB' }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT }} fontWeight={700} display="block">
                            التاريخ
                          </Typography>
                          <Typography variant="body2" fontWeight={700} sx={{ color: '#1E1B4B', fontFamily: FONT }}>
                            {formatDate(latestPayment.date)}
                          </Typography>
                        </Box>
                        {latestPayment.reason && (
                          <>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: '#E5E7EB' }} />
                            <Box flex={1} minWidth={0}>
                              <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT }} fontWeight={700} display="block">
                                السبب
                              </Typography>
                              <Typography variant="body2" noWrap sx={{ color: '#6B7280', fontFamily: FONT }}>
                                {latestPayment.reason}
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Paper>
                    )}
                  </Stack>

                  {/* Payments table */}
                  <Box sx={{ borderRadius: 2.5, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                    <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {['النوع', 'المبلغ', 'التاريخ', 'السبب'].map((col) => (
                              <TableCell key={col} sx={{
                                fontFamily: FONT, fontWeight: 700, color: '#0284C7',
                                bgcolor: '#E0F2FE', borderBottom: '2px solid #BAE6FD',
                                py: 1.2, fontSize: '0.75rem',
                              }}>
                                {col}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[...bus.payments]
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .map((record, idx) => (
                              <TableRow key={idx} sx={{
                                background: idx % 2 === 0 ? '#F8F9FF' : '#fff',
                                '&:last-child td': { borderBottom: 'none' },
                                '&:hover': { background: '#E0F2FE' },
                                transition: 'background 0.15s',
                              }}>
                                <TableCell sx={{ fontFamily: FONT, py: 1.2, borderColor: '#F0F1F5' }}>
                                  <Chip
                                    label={record.type || 'دفعة'}
                                    size="small"
                                    sx={{ bgcolor: '#E0F2FE', color: '#0284C7', fontWeight: 700, fontSize: '0.72rem', height: 22, fontFamily: FONT }}
                                  />
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT, py: 1.2, borderColor: '#F0F1F5', fontWeight: 800, color: '#0284C7' }}>
                                  {parseFloat(record.amount || 0).toLocaleString('en-US')} ₪
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT, py: 1.2, borderColor: '#F0F1F5', color: '#6B7280' }}>
                                  {formatDate(record.date)}
                                </TableCell>
                                <TableCell sx={{ fontFamily: FONT, py: 1.2, borderColor: '#F0F1F5', color: '#6B7280', maxWidth: 160 }}>
                                  <Typography variant="body2" noWrap sx={{ fontFamily: FONT, color: '#6B7280' }}>
                                    {record.reason || '—'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                </>
              ) : (
                <Box textAlign="center" py={4}>
                  <ReceiptIcon sx={{ fontSize: 48, color: '#C7CBD9', mb: 1 }} />
                  <Typography sx={{ color: '#6B7280', fontFamily: FONT }}>لا توجد مدفوعات مسجّلة لهذه الحافلة</Typography>
                </Box>
              )}
            </SectionCard>
          </motion.div>
        </Grid>

        {/* ── Students List ─────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <motion.div {...fadeUp(0.19)}>
            <SectionCard>
              <SectionHeader
                icon={<GroupIcon fontSize="small" />}
                title={`الطلاب المسجلون (${assignedStudents.length})`}
                color="#16A34A"
                tint="#DCFCE7"
                action={
                  <Stack useFlexGap direction="row" spacing={1}>
                    <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon />}
                      onClick={handlePrintStudents}
                      sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT, borderColor: '#16A34A', color: '#16A34A',
                        '&:hover': { bgcolor: '#DCFCE7', borderColor: '#16A34A' } }}>
                      طباعة القائمة
                    </Button>
                    <Button variant="contained" size="small" startIcon={<PersonAddIcon />}
                      onClick={openAssignDialog}
                      sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT,
                        bgcolor: '#16A34A', boxShadow: 'none', '&:hover': { bgcolor: '#15803D', boxShadow: 'none' } }}>
                      إضافة طلاب
                    </Button>
                  </Stack>
                }
              />

              {/* Search */}
              {assignedStudents.length > 5 && (
                <TextField
                  placeholder="ابحث باسم الطالب…"
                  size="small"
                  value={studentSearch}
                  onChange={e => setStudentSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2.5, fontFamily: FONT, bgcolor: '#F8F9FF' },
                  }}
                  sx={{
                    mb: 2, maxWidth: 320,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4338CA' },
                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4338CA' },
                  }}
                  fullWidth={false}
                />
              )}

              {filteredStudents.length > 0 ? (
                <Box sx={{ maxHeight: 480, overflowY: 'auto', pr: 0.5 }}>
                  <Grid container spacing={1.5}>
                    {filteredStudents.map((student, idx) => {
                      const name = `${student.first_name || ''} ${student.second_name || ''}`.trim();
                      const initials = [student.first_name?.[0], student.second_name?.[0]].filter(Boolean).join('');
                      const className = classMap.get(student.school_class) || 'غير محدد';
                      return (
                        <Grid item xs={12} sm={6} md={4} key={student.id}>
                          <Paper elevation={0} sx={{
                            p: 1.5, borderRadius: 2.5,
                            border: '1px solid #E5E7EB',
                            background: idx % 2 === 0 ? '#F8F9FF' : '#fff',
                            display: 'flex', alignItems: 'center', gap: 1.5,
                            transition: 'box-shadow 0.18s, background 0.18s, border-color 0.18s',
                            '&:hover': { boxShadow: '0 4px 14px rgba(67,56,202,0.12)', background: '#EEF2FF', borderColor: '#C7D2FE' },
                          }}>
                            <Avatar sx={{
                              width: 38, height: 38, fontSize: '0.9rem', fontWeight: 700,
                              bgcolor: `hsl(${(idx * 47) % 360},55%,65%)`,
                              color: '#fff', flexShrink: 0, fontFamily: FONT, cursor: 'pointer',
                            }} onClick={() => navigate(`/students/view/${student.id}`)}>
                              {initials || <PersonIcon fontSize="small" />}
                            </Avatar>
                            <Box flex={1} minWidth={0} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/students/view/${student.id}`)}>
                              <Typography variant="body2" fontWeight={700} noWrap sx={{ color: '#1E1B4B', fontFamily: FONT }}>{name}</Typography>
                              <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT }}>{className}</Typography>
                            </Box>
                            <Tooltip title="إزالة من الحافلة">
                              <IconButton size="small" onClick={() => handleRemoveStudent(student.id)}
                                sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEE2E2' }, flexShrink: 0 }}>
                                <PersonRemoveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ) : assignedStudents.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <PeopleIcon sx={{ fontSize: 48, color: '#C7CBD9', mb: 1 }} />
                  <Typography sx={{ color: '#6B7280', fontFamily: FONT }}>لا يوجد طلاب مسجلون في هذه الحافلة حالياً</Typography>
                </Box>
              ) : (
                <Box textAlign="center" py={3}>
                  <Typography sx={{ color: '#6B7280', fontFamily: FONT }}>لا توجد نتائج للبحث</Typography>
                </Box>
              )}
            </SectionCard>
          </motion.div>
        </Grid>

      </Grid>
     </Box>
    </Box>

    {/* ── Assign Students Dialog ─────────────────────────────────────────── */}
    <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, fontFamily: FONT, direction: 'rtl' } }}>
      <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700, color: '#1E1B4B', pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack useFlexGap direction="row" alignItems="center" spacing={1}>
            <PersonAddIcon sx={{ color: '#16A34A' }} />
            <Typography fontWeight={700} sx={{ fontFamily: FONT }}>إضافة / إزالة طلاب من الحافلة</Typography>
          </Stack>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        <TextField
          placeholder="ابحث باسم الطالب…"
          size="small"
          fullWidth
          value={dialogSearch}
          onChange={e => setDialogSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
              </InputAdornment>
            ),
            sx: { borderRadius: 2.5, fontFamily: FONT, bgcolor: '#F8F9FF' },
          }}
          sx={{ mb: 1.5, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' } }}
        />

        <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT, mb: 1, display: 'block' }}>
          {selectedIds.length} طالب محدد
        </Typography>

        <List dense sx={{ maxHeight: 380, overflowY: 'auto' }}>
          {dialogStudents.map((student, idx) => {
            const name = `${student.first_name || ''} ${student.second_name || ''}`.trim();
            const initials = [student.first_name?.[0], student.second_name?.[0]].filter(Boolean).join('');
            const className = classMap.get(student.school_class) || '';
            const checked = selectedIds.includes(student.id);
            return (
              <ListItem key={student.id} button onClick={() => handleToggleStudent(student.id)}
                sx={{
                  borderRadius: 2, mb: 0.5,
                  background: checked ? '#DCFCE7' : idx % 2 === 0 ? '#F8F9FF' : '#fff',
                  border: `1px solid ${checked ? '#BBF7D0' : '#E5E7EB'}`,
                  '&:hover': { background: checked ? '#DCFCE7' : '#EEF2FF' },
                }}>
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Avatar sx={{
                    width: 34, height: 34, fontSize: '0.8rem', fontWeight: 700,
                    bgcolor: `hsl(${(idx * 47) % 360},55%,65%)`, color: '#fff', fontFamily: FONT,
                  }}>
                    {initials || <PersonIcon fontSize="small" />}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={<Typography variant="body2" fontWeight={700} sx={{ fontFamily: FONT, color: '#1E1B4B' }}>{name}</Typography>}
                  secondary={<Typography variant="caption" sx={{ fontFamily: FONT, color: '#6B7280' }}>{className}</Typography>}
                />
                <ListItemSecondaryAction>
                  <Checkbox checked={checked} onChange={() => handleToggleStudent(student.id)}
                    sx={{ color: '#16A34A', '&.Mui-checked': { color: '#16A34A' } }} />
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
          {dialogStudents.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography sx={{ color: '#9CA3AF', fontFamily: FONT }}>لا توجد نتائج</Typography>
            </Box>
          )}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={() => setDialogOpen(false)} variant="outlined"
          sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT,
            borderColor: '#E5E7EB', color: '#6B7280', '&:hover': { borderColor: '#9CA3AF' } }}>
          إلغاء
        </Button>
        <Button onClick={handleSaveAssignments} variant="contained" disabled={isSaving}
          sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT,
            bgcolor: '#16A34A', boxShadow: 'none', '&:hover': { bgcolor: '#15803D', boxShadow: 'none' } }}>
          {isSaving ? 'جاري الحفظ…' : 'حفظ'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default ViewBusPage;
